import type { WorkflowItem } from "../hooks/useWorkflows";

function humanReadableCron(expression: string): string {
  const parts = expression.split(" ");
  if (parts.length !== 5) return expression;

  const min = parts[0] ?? "*";
  const hour = parts[1] ?? "*";
  const dow = parts[4] ?? "*";

  const timeStr =
    hour !== "*" && min !== "*"
      ? `${hour.padStart(2, "0")}:${min.padStart(2, "0")}`
      : null;

  if (dow === "1-5" && timeStr) return `Weekdays at ${timeStr}`;
  if (dow === "*" && timeStr) return `Daily at ${timeStr}`;
  if (dow === "1" && timeStr) return `Mondays at ${timeStr}`;

  return expression;
}

export function WorkflowCard({
  workflow,
  onToggle,
  onDelete,
  onSelect,
}: {
  workflow: WorkflowItem;
  onToggle: (id: string, enabled: boolean) => void;
  onDelete: (id: string) => void;
  onSelect: (id: string) => void;
}) {
  const trigger = workflow.definition.trigger;
  const schedule =
    trigger.type === "cron" && trigger.expression
      ? humanReadableCron(trigger.expression)
      : "Manual";

  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <button
          type="button"
          className="text-left"
          onClick={() => onSelect(workflow.id)}
        >
          <h3 className="font-medium text-gray-900">{workflow.name}</h3>
          {workflow.description && (
            <p className="mt-1 text-sm text-gray-500">{workflow.description}</p>
          )}
        </button>
        <label className="relative inline-flex cursor-pointer items-center">
          <input
            type="checkbox"
            checked={workflow.enabled}
            onChange={() => onToggle(workflow.id, !workflow.enabled)}
            className="peer sr-only"
          />
          <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full" />
        </label>
      </div>
      <div className="mt-3 flex items-center gap-4 text-xs text-gray-400">
        <span>{schedule}</span>
        {trigger.type === "cron" && trigger.timezone && (
          <span>{trigger.timezone}</span>
        )}
        <span>
          {workflow.definition.steps.length} step
          {workflow.definition.steps.length !== 1 ? "s" : ""}
        </span>
        <button
          type="button"
          onClick={() => onDelete(workflow.id)}
          className="ml-auto text-red-400 hover:text-red-600"
        >
          Delete
        </button>
      </div>
    </div>
  );
}
